/**
 * Product Options Gallery Sync
 * Syncs Avis product option swatch selections with product gallery
 * Matches swatch value to image alt text (case-insensitive) and switches gallery
 * Works with any swatch option field (Hardwood Material, Size, Color, etc.)
 */

(function () {
   'use strict';

   class ProductOptionsGallerySync {
      constructor() {
         this.gallerySelector = 'media-gallery';
         this.swatchContainerClass = 'ap-options__swatch-container';
         this.targetSlideIndex = null; // Track intended slide
         this.verificationTimeout = null;
         this.maxRetries = 10;
         this.currentSelectedValue = null; // Store selected Avis option
         // console.log('[Gallery Sync] Initializing...');
         this.init();
      }

      init() {
         // Wait for Avis swatches to be rendered
         this.waitForSwatches();

         // Watch for gallery replacement (when variant changes)
         this.observeGalleryReplacement();
         // console.log('[Gallery Sync] Initialized');
      }

      waitForSwatches() {
         // Listen for Avis Product Options ready event
         document.addEventListener('avisOptionsLoaded', () => {
            // console.log('[Gallery Sync] Avis options loaded');
            this.attachSwatchListeners();
         });

         // Also try immediately in case Avis already loaded
         // this.attachSwatchListeners();
      }

      attachSwatchListeners() {
         const swatchContainers = document.querySelectorAll(`.${this.swatchContainerClass}`);

         // console.log('[Gallery Sync] Found swatch containers:', swatchContainers.length);

         if (swatchContainers.length === 0) {
            return false;
         }

         let totalSwatches = 0;

         swatchContainers.forEach(container => {
            const inputs = container.querySelectorAll('input[type="radio"]');

            inputs.forEach(input => {
               totalSwatches++;
               input.addEventListener('change', e => {
                  // console.log('[Gallery Sync] Swatch changed to:', e.target.value);
                  this.handleSwatchChange(e.target.value);
               });
            });
         });

         // console.log('[Gallery Sync] Total swatches found:', totalSwatches);

         return totalSwatches > 0;
      }

      handleSwatchChange(selectedValue, shouldAnimate = true) {
         // Store the selected value for persistence across variant changes
         this.currentSelectedValue = selectedValue;

         const mediaGallery = document.querySelector(this.gallerySelector);
         // console.log('[Gallery Sync] Media gallery found:', !!mediaGallery);
         if (!mediaGallery) return;

         const slideshow = mediaGallery.slideshow;
         // console.log('[Gallery Sync] Slideshow found:', !!slideshow);
         if (!slideshow) return;

         const media = mediaGallery.media;
         // console.log('[Gallery Sync] Media items:', media?.length);
         if (!media || !media.length) return;

         // Find matching image by alt text (case-insensitive)
         const matchedIndex = this.findMatchingImageIndex(media, selectedValue);
         // console.log('[Gallery Sync] Matched index:', matchedIndex);

         if (matchedIndex !== -1) {
            // Store the target index
            this.targetSlideIndex = matchedIndex;

            // Attempt to switch with verification
            this.selectSlideWithVerification(slideshow, matchedIndex, 0, shouldAnimate);
         } else {
            console.warn('[Gallery Sync] No matching image found for:', selectedValue);
         }
      }

      selectSlideWithVerification(slideshow, targetIndex, attemptCount = 0, shouldAnimate = true) {
         // Clear any pending verification
         if (this.verificationTimeout) {
            clearTimeout(this.verificationTimeout);
         }

         // Select the slide
         slideshow.select(targetIndex, undefined, { animate: shouldAnimate });

         // Verify after a short delay
         this.verificationTimeout = setTimeout(
            () => {
               // Check if we're on the correct slide
               const currentIndex = slideshow.current;
               // console.log('[Gallery Sync] Current slide index:', currentIndex);

               if (currentIndex !== targetIndex && attemptCount < this.maxRetries) {
                  // console.log(`[Gallery Sync] Verification failed. Expected ${targetIndex}, got ${currentIndex}. Retry ${attemptCount + 1}`);

                  // Retry (shouldAnimate already handled in the method)
                  this.selectSlideWithVerification(slideshow, targetIndex, attemptCount + 1, shouldAnimate);
               } else if (currentIndex === targetIndex) {
                  // console.log(`[Gallery Sync] Successfully switched to slide ${targetIndex}`);
               } else {
                  console.warn(`[Gallery Sync] Failed to switch to slide ${targetIndex} after ${this.maxRetries} attempts`);
               }
            },
            shouldAnimate ? 150 : 50,
         ); // Longer delay if animating
      }

      findMatchingImageIndex(media, searchValue) {
         const normalizedSearch = searchValue.toLowerCase().trim();
         // console.log('[Gallery Sync] Searching for:', normalizedSearch);

         const altTexts = [];

         for (let i = 0; i < media.length; i++) {
            const mediaElement = media[i];

            // Find img tag within the media element
            const img = mediaElement.querySelector('img');
            if (!img) {
               altTexts.push(`[${i}] No img tag`);
               continue;
            }

            const alt = img.getAttribute('alt');
            if (!alt) {
               altTexts.push(`[${i}] No alt`);
               continue;
            }

            const normalizedAlt = alt.toLowerCase().trim();
            altTexts.push(`[${i}] "${alt}"`);

            // Exact match (case-insensitive)
            if (normalizedAlt === normalizedSearch) {
               // console.log('[Gallery Sync] Match found at index:', i);
               return i;
            }
         }

         // console.log('[Gallery Sync] All alt texts:', altTexts);
         return -1;
      }

      waitForGalleryAndSync(gallery, maxAttempts = 20, attempt = 0) {
         // Check if gallery has slideshow and media ready
         if (gallery.slideshow && gallery.media && gallery.media.length > 0) {
            // Gallery is ready, sync to stored selection
            if (this.currentSelectedValue) {
               console.log('[Gallery Sync] Re-syncing to stored selection:', this.currentSelectedValue);
               // Pass false for shouldAnimate to instantly switch without animation
               this.handleSwatchChange(this.currentSelectedValue, false);
            }
         } else if (attempt < maxAttempts) {
            // Gallery not ready, retry
            setTimeout(() => {
               this.waitForGalleryAndSync(gallery, maxAttempts, attempt + 1);
            }, 100);
         } else {
            console.warn('[Gallery Sync] Gallery failed to initialize after', maxAttempts, 'attempts');
         }
      }

      observeGalleryReplacement() {
         // Watch for gallery DOM changes (variant updates replace entire gallery)
         const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
               mutation.addedNodes.forEach(node => {
                  // Check if a new media-gallery was added
                  if (node.nodeType === 1 && node.matches && node.matches(this.gallerySelector)) {
                     console.log('[Gallery Sync] New gallery detected, waiting for initialization...');
                     // Gallery replaced - re-attach listeners
                     this.attachSwatchListeners();
                     // Re-sync to stored Avis selection after gallery is ready
                     this.waitForGalleryAndSync(node);
                  }
               });
            });
         });

         // Observe the product section for changes
         const productSection = document.querySelector('.media-gallery-container');
         if (productSection) {
            observer.observe(productSection, {
               childList: true,
               subtree: true,
            });
         }
      }
   }

   // Initialize when DOM is ready
   if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
         new ProductOptionsGallerySync();
      });
   } else {
      new ProductOptionsGallerySync();
   }
})();
